import { Monster, MonsterStats, calculateSpecialAttackCount } from '../types/Monster';
import { MONSTER_DATABASE, ABILITY_UP_VALUES } from './monsters';
import { STAT_CAP } from './survivalConfig';

export const MAX_GROWTH_STAGES = 7;

/**
 * 能力UP値の型
 */
interface AbilityUpValue {
  hp: number;
  strength: number;
  special: number;
  speed: number;
  toughness: number;
}

/**
 * 指定成長段階のステータスを計算する
 * specialAttackCountは成長後のspecialから自動算出: floor(special / 50)
 * 成長段階の上限なし（サバイバルモード対応）。各ステータスは999が上限。
 */
export function calculateGrownStats(
  baseStats: MonsterStats,
  abilityUp: AbilityUpValue,
  stages: number,
): MonsterStats {
  const clampedStages = Math.max(0, stages);
  const cap = (val: number) => Math.min(val, STAT_CAP);
  const grownSpecial = cap(baseStats.special + abilityUp.special * clampedStages);
  return {
    hp: cap(baseStats.hp + abilityUp.hp * clampedStages),
    strength: cap(baseStats.strength + abilityUp.strength * clampedStages),
    special: grownSpecial,
    speed: cap(baseStats.speed + abilityUp.speed * clampedStages),
    toughness: cap(baseStats.toughness + abilityUp.toughness * clampedStages),
    specialAttackCount: calculateSpecialAttackCount(grownSpecial),
  };
}

/**
 * 指定成長段階のパラメータを持つモンスターを返す
 */
export function getMonsterWithGrownStats(monsterId: string, stages: number): Monster | undefined {
  const base = MONSTER_DATABASE.find((m) => m.id === monsterId);
  if (!base) return undefined;

  const abilityUp = ABILITY_UP_VALUES[monsterId];
  if (!abilityUp) return undefined;

  return {
    ...base,
    stats: calculateGrownStats(base.stats, abilityUp, stages),
  };
}

/**
 * 最終パラメータ（全ステージクリア後）を持つモンスターを返す
 */
export function getMonsterWithFinalStats(monsterId: string): Monster | undefined {
  const base = MONSTER_DATABASE.find((m) => m.id === monsterId);
  if (!base) return undefined;

  const abilityUp = ABILITY_UP_VALUES[monsterId];
  if (!abilityUp) return undefined;

  return {
    ...base,
    stats: calculateGrownStats(base.stats, abilityUp, MAX_GROWTH_STAGES),
  };
}

/** パラメータ成長差分の型 */
export interface StatsDiff {
  hp: { before: number; after: number; gain: number };
  strength: { before: number; after: number; gain: number };
  special: { before: number; after: number; gain: number };
  speed: { before: number; after: number; gain: number };
  toughness: { before: number; after: number; gain: number };
}

/**
 * ステージクリア前後のパラメータ差分を計算する
 * stagesAfter: クリア後のclearedStages（1以上）
 * stagesAfter=0 の場合は before=after=基礎値（gain=0）
 */
export function calculateStatsDiff(monsterId: string, stagesAfter: number): StatsDiff | undefined {
  const base = MONSTER_DATABASE.find((m) => m.id === monsterId);
  if (!base) return undefined;

  const abilityUp = ABILITY_UP_VALUES[monsterId];
  if (!abilityUp) return undefined;

  const cappedAfter = Math.min(stagesAfter, MAX_GROWTH_STAGES);
  const cappedBefore = Math.min(Math.max(0, stagesAfter - 1), MAX_GROWTH_STAGES);
  const before = calculateGrownStats(base.stats, abilityUp, cappedBefore);
  const after = calculateGrownStats(base.stats, abilityUp, cappedAfter);

  return {
    hp:        { before: before.hp,        after: after.hp,        gain: after.hp        - before.hp },
    strength:  { before: before.strength,  after: after.strength,  gain: after.strength  - before.strength },
    special:   { before: before.special,   after: after.special,   gain: after.special   - before.special },
    speed:     { before: before.speed,     after: after.speed,     gain: after.speed     - before.speed },
    toughness: { before: before.toughness, after: after.toughness, gain: after.toughness - before.toughness },
  };
}

/**
 * 全モンスターの最終パラメータ版データベース
 */
export const FINAL_MONSTER_DATABASE: Monster[] = MONSTER_DATABASE
  .map((m) => getMonsterWithFinalStats(m.id))
  .filter((m): m is Monster => m !== undefined);
